import { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { localizePath, stripLocale, type Locale } from '../../lib/locale';
import { Link } from '../base/Link';
import { useTranslation } from 'react-i18next';

export default function Header() {
  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileProductOpen, setIsMobileProductOpen] = useState(false);
  const [isMobileLanguageOpen, setIsMobileLanguageOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  
  // 添加定时器引用
  const productDropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const languageDropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = (path: string) => stripLocale(location.pathname) === path;

  // 语言切换函数
  // Each language has its own URL (/en/...), so switching navigates to the counterpart page.
  const changeLanguage = (lang: Locale) => {
    navigate(localizePath(location.pathname, lang) + location.search + location.hash);
    setIsLanguageDropdownOpen(false);
  };

  // 获取当前语言显示名称
  const getCurrentLanguageName = () => {
    return i18n.language === 'zh' ? '中文' : 'English';
  };

  // 产品下拉菜单事件处理
  const handleProductMouseEnter = () => {
    if (productDropdownTimer.current) {
      clearTimeout(productDropdownTimer.current);
    }
    setIsProductDropdownOpen(true);
  };

  const handleProductMouseLeave = () => {
    productDropdownTimer.current = setTimeout(() => {
      setIsProductDropdownOpen(false);
    }, 200); // 200ms延迟
  };

  // 语言下拉菜单事件处理
  const handleLanguageMouseEnter = () => {
    if (languageDropdownTimer.current) {
      clearTimeout(languageDropdownTimer.current);
    }
    setIsLanguageDropdownOpen(true);
  };

  const handleLanguageMouseLeave = () => {
    languageDropdownTimer.current = setTimeout(() => {
      setIsLanguageDropdownOpen(false);
    }, 200); // 200ms延迟
  };

  // 移动端菜单切换
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // 移动端产品菜单切换
  const toggleMobileProduct = () => {
    setIsMobileProductOpen(!isMobileProductOpen);
  };

  // 移动端语言菜单切换
  const toggleMobileLanguage = () => {
    setIsMobileLanguageOpen(!isMobileLanguageOpen);
  };

  // 关闭移动端菜单
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileProductOpen(false);
    setIsMobileLanguageOpen(false);
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <nav className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/logo.svg" 
              alt="ORedact Logo" 
              className="w-10 h-10"
            />
            <span
              className="text-2xl font-bold text-gray-900" 
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '0.02em' }}
            >
              {t('nav.brandName')}
            </span>
          </Link>

          {/* Navigation Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* 产品模块下拉菜单 */}
            <div 
              className="relative"
              onMouseEnter={handleProductMouseEnter}
              onMouseLeave={handleProductMouseLeave}
            >
              <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer py-2">
                <span>{t('nav.products')}</span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {isProductDropdownOpen && (
                <div 
                  className="absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-100 py-2"
                  onMouseEnter={handleProductMouseEnter}
                  onMouseLeave={handleProductMouseLeave}
                >
                  <Link 
                    to="/video-redaction" 
                    className={`block px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${isActive('/video-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-video-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.videoRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.videoRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link 
                    to="/image-redaction" 
                    className={`block px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${isActive('/image-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-image-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.imageRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.imageRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link 
                    to="/text-redaction" 
                    className={`block px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${isActive('/text-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-file-text-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.textRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.textRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link 
                    to="/file-redaction" 
                    className={`block px-4 py-3 text-sm hover:bg-gray-50 transition-colors ${isActive('/file-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-file-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.fileRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.fileRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            <Link 
              to="/examples" 
              className={`text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer ${isActive('/examples') ? 'text-blue-600' : ''}`}
            >
              {t('nav.examples')}
            </Link>

            <Link 
              to="/pricing" 
              className={`text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer ${isActive('/pricing') ? 'text-blue-600' : ''}`}
            >
              {t('nav.pricing')}
            </Link>

            <Link 
              to="/about" 
              className={`text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer ${isActive('/about') ? 'text-blue-600' : ''}`}
            >
              {t('nav.about')}
            </Link>

            {/* 语言切换下拉菜单 */}
            <div 
              className="relative"
              onMouseEnter={handleLanguageMouseEnter}
              onMouseLeave={handleLanguageMouseLeave}
            >
              <button className="flex items-center space-x-1 text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer py-2">
                <i className="ri-global-line text-lg"></i>
                <span>{getCurrentLanguageName()}</span>
                <i className="ri-arrow-down-s-line text-sm"></i>
              </button>
              {isLanguageDropdownOpen && (
                <div 
                  className="absolute top-full right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-gray-100 py-2"
                  onMouseEnter={handleLanguageMouseEnter}
                  onMouseLeave={handleLanguageMouseLeave}
                >
                  <button 
                    onClick={() => changeLanguage('zh')}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${i18n.language === 'zh' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    中文
                  </button>
                  <button 
                    onClick={() => changeLanguage('en')}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${i18n.language === 'en' ? 'text-blue-600 bg-blue-50' : 'text-gray-700'}`}
                  >
                    English
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 登录注册按钮 - 桌面端 */}
          <div className="hidden lg:flex items-center space-x-4">
            <a href="https://damaya.cn" className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">{t('nav.damaya')}</a>
            <a 
              href="https://console.oredact.com/login" 
              className="text-gray-700 hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.login')}
            </a>
            <Link 
              to="/pricing#contact"
              className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              {t('nav.register')}
            </Link>
          </div>

          {/* 移动端菜单按钮 */}
          <button
            onClick={toggleMobileMenu}
            className="lg:hidden p-2 text-gray-700 hover:text-blue-600 transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <i className="ri-close-line text-2xl"></i>
            ) : (
              <i className="ri-menu-line text-2xl"></i>
            )}
          </button>
        </div>

        {/* 移动端菜单 */}
        {isMobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-100">
            {/* 产品模块 */}
            <div className="py-2">
              <button
                onClick={toggleMobileProduct}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <i className="ri-apps-line"></i>
                  <span>{t('nav.products')}</span>
                </span>
                <i className={`ri-arrow-${isMobileProductOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {isMobileProductOpen && (
                <div className="pl-8 pr-4 py-2 space-y-1">
                  <Link
                    to="/video-redaction"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/video-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-video-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.videoRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.videoRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/image-redaction"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/image-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-image-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.imageRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.imageRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/text-redaction"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/text-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-file-text-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.textRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.textRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                  <Link
                    to="/file-redaction"
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 rounded-lg text-sm transition-colors ${isActive('/file-redaction') ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <i className="ri-file-line text-lg"></i>
                      <div>
                        <div className="font-medium">{t('products.fileRedaction')}</div>
                        <div className="text-xs text-gray-500">{t('products.fileRedactionDesc')}</div>
                      </div>
                    </div>
                  </Link>
                </div>
              )}
            </div>

            {/* 其他导航链接 */}
            <Link
              to="/examples"
              onClick={closeMobileMenu}
              className={`block px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors ${isActive('/examples') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              {t('nav.examples')}
            </Link>
            <Link
              to="/pricing"
              onClick={closeMobileMenu}
              className={`block px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors ${isActive('/pricing') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              {t('nav.pricing')}
            </Link>
            <Link
              to="/about"
              onClick={closeMobileMenu}
              className={`block px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors ${isActive('/about') ? 'text-blue-600 bg-blue-50' : ''}`}
            >
              {t('nav.about')}
            </Link>

            {/* 语言切换 */}
            <div className="py-2">
              <button
                onClick={toggleMobileLanguage}
                className="w-full flex items-center justify-between px-4 py-3 text-gray-700 hover:text-blue-600 transition-colors"
              >
                <span className="flex items-center space-x-2">
                  <i className="ri-global-line"></i>
                  <span>{getCurrentLanguageName()}</span>
                </span>
                <i className={`ri-arrow-${isMobileLanguageOpen ? 'up' : 'down'}-s-line`}></i>
              </button>
              {isMobileLanguageOpen && (
                <div className="pl-8 pr-4 py-2 space-y-1">
                  <button
                    onClick={() => {
                      changeLanguage('zh');
                      setIsMobileLanguageOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${i18n.language === 'zh' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    中文
                  </button>
                  <button
                    onClick={() => {
                      changeLanguage('en');
                      setIsMobileLanguageOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 rounded-lg text-sm transition-colors ${i18n.language === 'en' ? 'text-blue-600 bg-blue-50' : 'text-gray-700 hover:bg-gray-50'}`}
                  >
                    English
                  </button>
                </div>
              )}
            </div>

            {/* 登录注册按钮 - 移动端 */}
            <div className="px-4 pt-4 space-y-3 border-t border-gray-100 mt-2">
              <a 
                href="https://console.oredact.com/login" 
                className="block w-full text-center text-gray-700 hover:text-blue-600 transition-colors py-2"
              >
                {t('nav.login')}
              </a>
              <Link 
                to="/pricing#contact"
                className="block w-full text-center bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('nav.register')}
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}